/**
 * 주간 보고 초안(Draft) 시드, 목록 편집, 다시 읽기, 지난 보고 미결 가져오기, 마감 스냅샷 조립.
 */
import { addDays, diffDays, isISODate, toDay, weekdayOfDay, type ISODate } from "@/engine/date";
import type {
  Baseline,
  Project,
  ProjectRecord,
  ScheduledTask,
  Task,
  UserResource,
  WeeklyAgendaLine,
  WeeklyIssueLine,
  WeeklyLineOrigin,
  WeeklyReportDraft,
  WeeklyReportSnapshot,
  WeeklyRequestLine,
  WeeklySectionVisibility,
  WeeklyTaskLine,
  WeeklyWbsLine,
} from "@/engine/types";
import { costEvmOf, UNAVAILABLE_COST } from "@/features/pm/cost-evm";
import { accountableName } from "@/features/pm/raci";
import { buildReportContext, REPORT_RULES, suggestPeriodName, type ReportMetrics } from "@/features/reports/report-model";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { fmtSpi } from "@/lib/format";
import { IMPACT_LABEL, recordCode, recordPhase } from "@/store/record-ops";
import { newId } from "@/store/tree-ops";
import type { WeeklyV2HistoryPoint, WeeklyV2WeekSnapshot } from "./weekly-report-v2-data";
import { findWeeklySnapshot, formatReportNo, readFrozenReport } from "./weekly-snapshot";

export const DEFAULT_WEEKLY_SECTIONS: WeeklySectionVisibility = {
  cost: false,
  wbs: false,
  history: true,
};

export const INITIAL_CURRENT_TASK_LIMIT = 5;

export type WeeklyListKey = "prevTasks" | "currentTasks" | "nextTasks" | "agendas" | "issues" | "requests" | "wbs";

const dash = { deliverable: "—", task: "—" };

function dayOf(value: string | undefined): ISODate | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  return isISODate(day) ? day : null;
}

export function weekRangeOf(statusDate: ISODate): { start: ISODate; end: ISODate } {
  const wd = weekdayOfDay(toDay(statusDate));
  const start = addDays(statusDate, wd === 0 ? -6 : 1 - wd);
  return { start, end: addDays(start, 6) };
}

/** "2026-10-20" -> "10월 3주차" */
export function shortWeekLabel(statusDate: string): string {
  const full = suggestPeriodName(statusDate);
  return full.replace(/\s*주간 보고$/, "");
}

export function computeVerdictOf(metrics: ReportMetrics): WeeklyReportDraft["statusVerdict"] {
  if (!metrics.hasBaseline) return "unknown";
  const spi = metrics.spi;
  if ((spi != null && spi < REPORT_RULES.spiRisk) || metrics.finishVarianceDays > REPORT_RULES.finishRiskDays) return "critical";
  if ((spi != null && spi < REPORT_RULES.spiWatch) || metrics.finishVarianceDays > 0 || metrics.delayCount > 0) return "watch";
  return "normal";
}

const SAMPLE_SAFETY_WEEK_MEMO =
  "이번 주는 심사 범위를 파일럿 셀로 좁힙니다. 펜스 높이가 도면과 다르고, 야간조 교육이 빠져 있어 증빙 전에 둘을 맞춥니다.";

/** 샘플 플랜의 현재 주만 평가 메모를 채운다. 다른 주는 빈 칸이다. */
export function seedWeekMemo(projectId: string, statusDate: ISODate, planStatusDate: ISODate): string {
  if (projectId === "sample-robot-safety" && statusDate === planStatusDate) return SAMPLE_SAFETY_WEEK_MEMO;
  return "";
}

export function computeSummaryOf(metrics: ReportMetrics): string {
  if (!metrics.hasBaseline) return "기준선이 없어 계획 대비는 판정할 수 없습니다.";
  return `SPI ${fmtSpi(metrics.spi)} · 종료 편차 ${metrics.finishVarianceDays}일 · 핵심경로 ${metrics.criticalCount} · 지연 ${metrics.delayCount}`;
}

function personName(resources: Record<string, UserResource>, id: string | undefined): string | null {
  if (!id) return null;
  const name = resources[id]?.displayName?.trim();
  return name || null;
}

export function seedPmNames(project: Project, resources: Record<string, UserResource>): { clientPm: string; vendorPm: string } {
  const rows = project.resourceAssignments ?? [];
  const client = rows.find((row) => row.projectRole.includes("고객") || row.projectRole.includes("발주"));
  const vendor = rows.find((row) => row.accessRole === "pm") ?? rows[0];
  return {
    clientPm: personName(resources, client?.resourceId) ?? "발주 역할이 없습니다.",
    vendorPm: personName(resources, vendor?.resourceId) ?? "—",
  };
}

export function pmPersonOptions(project: Project, resources: Record<string, UserResource>): { clientOptions: string[]; vendorOptions: string[] } {
  const names = new Set<string>();
  for (const row of project.resourceAssignments ?? []) {
    const name = personName(resources, row.resourceId);
    if (name) names.add(name);
  }
  for (const sh of project.stakeholders ?? []) {
    if (sh.name?.trim()) names.add(sh.name.trim());
  }
  for (const res of Object.values(resources)) {
    if (res.displayName?.trim()) names.add(res.displayName.trim());
  }
  const list = [...names];
  return {
    clientOptions: ["발주 역할이 없습니다.", ...list],
    vendorOptions: list.length > 0 ? list : ["—"],
  };
}

function taskOf(project: Project, id: string): Task | undefined {
  return project.tasks.find((task) => task.id === id);
}

function assigneeOf(task: Task, resources: Record<string, UserResource>): string {
  return task.assignee?.trim() || personName(resources, task.assignedResourceId) || "—";
}

function buildWbsLine(
  project: Project,
  scheduled: ScheduledTask,
  task: Task,
  baseline: Baseline | null,
  resources: Record<string, UserResource>,
  order: number,
  included = true,
): WeeklyWbsLine {
  const base = baseline?.tasks[task.id];
  const started = scheduled.percentComplete > 0;
  const done = scheduled.percentComplete >= 100;
  const assignee = assigneeOf(task, resources);
  return {
    id: task.id,
    sourceId: task.id,
    isPhase: scheduled.isSummary,
    code: scheduled.wbs,
    name: task.name,
    planStart: base ? formatDate(base.start, "numeric") : "—",
    planEnd: base ? formatDate(base.finish, "numeric") : "—",
    actualStart: started ? formatDate(scheduled.start, "numeric") : "—",
    actualEnd: done ? formatDate(scheduled.finish, "numeric") : started ? "진행중" : "—",
    progress: Math.round(scheduled.percentComplete),
    accountable: accountableName(project, task.id) ?? (assignee === "—" ? "—" : assignee),
    pm: { task: assignee, deliverable: "—" },
    design: task.deliverable?.trim() ? { deliverable: task.deliverable.trim(), task: "—" } : dash,
    dev: scheduled.critical ? { deliverable: "핵심경로", task: "—" } : dash,
    included,
    order,
  };
}

function seedWbsLines(
  project: Project,
  scheduleTasks: Map<string, ScheduledTask>,
  order: readonly string[],
  baseline: Baseline | null,
  resources: Record<string, UserResource>,
): WeeklyWbsLine[] {
  const rows: WeeklyWbsLine[] = [];
  for (const id of order) {
    const scheduled = scheduleTasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || scheduled.depth > 1) continue;
    rows.push(buildWbsLine(project, scheduled, task, baseline, resources, rows.length, true));
  }
  return rows;
}

function issueStatusOf(record: ProjectRecord, asOf: ISODate): WeeklyIssueLine["status"] {
  if (recordPhase(record.status) === "closed") return "resolved";
  if (record.due && record.due < asOf) return "delayed";
  if (record.status === "inProgress" || record.status === "mitigating") return "inProgress";
  return "open";
}

function issueSeverityOf(record: ProjectRecord): WeeklyIssueLine["severity"] {
  if (record.severity === "high") return "High";
  if (record.severity === "low") return "Low";
  if (record.severity === "medium") return "Medium";
  return null;
}

function firstSentence(text: string | undefined): string {
  if (!text) return "";
  const trimmed = text.trim();
  if (!trimmed) return "";
  return trimmed;
}

function issueActionSeed(record: ProjectRecord): string {
  const text =
    firstSentence(record.sections.resolution) ||
    firstSentence(record.sections.mitigation) ||
    firstSentence(record.sections.cause) ||
    firstSentence(record.sections.summary);
  return text || "—";
}

function inWeekClosed(record: ProjectRecord, weekStart: ISODate, weekEnd: ISODate): boolean {
  const closed = dayOf(record.closedAt);
  return !!closed && closed >= weekStart && closed <= weekEnd;
}

function buildIssueLineFromRecord(
  record: ProjectRecord,
  asOf: ISODate,
  order: number,
  included = true,
  origin: WeeklyLineOrigin = "auto",
): WeeklyIssueLine {
  const opened = dayOf(record.createdAt) ?? asOf;
  return {
    id: record.id,
    sourceId: record.id,
    code: recordCode(record),
    date: formatDate(opened, "numeric"),
    openedDate: opened,
    title: record.title || "제목 없음",
    severity: issueSeverityOf(record),
    owner: record.assignees.filter(Boolean).join(", ") || "—",
    action: issueActionSeed(record),
    status: issueStatusOf(record, asOf),
    included,
    order,
    origin,
  };
}

function seedIssueLines(project: Project, asOf: ISODate, weekStart: ISODate, weekEnd: ISODate): WeeklyIssueLine[] {
  const candidates = (project.records ?? []).filter((record) => {
    if (record.kind !== "issue" && record.kind !== "risk") return false;
    const opened = dayOf(record.createdAt);
    if (!opened || opened > asOf) return false;
    if (recordPhase(record.status) !== "closed") return true;
    return inWeekClosed(record, weekStart, weekEnd);
  });

  const sevRank = (sev: ProjectRecord["severity"]): number => {
    if (sev === "high") return 0;
    if (sev === "medium") return 1;
    if (sev === "low") return 2;
    return 3;
  };

  candidates.sort((a, b) => {
    const aClosed = recordPhase(a.status) === "closed" ? 1 : 0;
    const bClosed = recordPhase(b.status) === "closed" ? 1 : 0;
    if (aClosed !== bClosed) return aClosed - bClosed;
    const sr = sevRank(a.severity) - sevRank(b.severity);
    if (sr !== 0) return sr;
    return (a.createdAt || "").localeCompare(b.createdAt || "");
  });

  return candidates.map((record, idx) => buildIssueLineFromRecord(record, asOf, idx, true, "auto"));
}

function requestStatusOf(record: ProjectRecord, asOf: ISODate): WeeklyRequestLine["status"] {
  if (record.due && record.due < asOf) return "overdue";
  if (record.status === "reviewing") return "reviewing";
  return "pending";
}

function requestImpactSeed(record: ProjectRecord): string {
  const analysis = record.sections.impactAnalysis?.trim();
  if (analysis) return analysis;
  if (record.impacts.length) return record.impacts.map((impact) => IMPACT_LABEL[impact]).join(", ");
  return "—";
}

export function isOpenRequestRecord(record: ProjectRecord, asOf: ISODate): boolean {
  const opened = dayOf(record.createdAt);
  if (!opened || opened > asOf) return false;
  if (record.kind === "change") return recordPhase(record.status) !== "closed";
  return record.kind === "decision" && record.status === "proposed";
}

function buildRequestLineFromRecord(
  record: ProjectRecord,
  asOf: ISODate,
  order: number,
  included = true,
  origin: WeeklyLineOrigin = "auto",
): WeeklyRequestLine {
  const opened = dayOf(record.createdAt) ?? asOf;
  return {
    id: record.id,
    sourceId: record.id,
    code: recordCode(record),
    date: formatDate(opened, "numeric"),
    title: record.title || "제목 없음",
    neededBy: record.due ? formatDate(record.due, "numeric") : "—",
    target: record.assignees.filter(Boolean).join(", ") || "—",
    status: requestStatusOf(record, asOf),
    impact: requestImpactSeed(record),
    included,
    order,
    origin,
  };
}

function seedRequestLines(project: Project, asOf: ISODate): WeeklyRequestLine[] {
  return (project.records ?? [])
    .filter((record) => isOpenRequestRecord(record, asOf))
    .map((record, idx) => buildRequestLineFromRecord(record, asOf, idx, true, "auto"));
}

/**
 * 이 문서에 포함된 미결 이슈만으로 연령 문장을 계산한다.
 */
export function computeAgingNoteFromIssues(
  issues: readonly Pick<WeeklyIssueLine, "included" | "status" | "openedDate" | "date">[],
  asOf: ISODate,
): string {
  const bands = [0, 0, 0, 0];
  for (const issue of issues) {
    if (!issue.included) continue;
    if (issue.status === "resolved") continue;
    const rawOpened = issue.openedDate ?? (isISODate(issue.date) ? issue.date : asOf);
    const age = Math.max(0, diffDays(rawOpened, asOf));
    const index = age <= 7 ? 0 : age <= 14 ? 1 : age <= 28 ? 2 : 3;
    bands[index] = (bands[index] ?? 0) + 1;
  }
  const [withinWeek, fortnight, month, older] = bands;
  return `미결 연령 7일 이내 ${withinWeek ?? 0} · 8–14일 ${fortnight ?? 0} · 15–28일 ${month ?? 0} · 29일 이상 ${older ?? 0}`;
}

/**
 * 기준일 바로 이전의 마감본을 찾는다. 같은 기준일에 정정본이 있으면 마지막 마감본을 돌려준다.
 */
export function findPreviousClosedSnapshot(
  project: Pick<Project, "weeklySnapshots">,
  beforeStatusDate: ISODate,
): WeeklyReportSnapshot | undefined {
  const candidates = (project.weeklySnapshots ?? []).filter((s) => s.statusDate < beforeStatusDate);
  if (candidates.length === 0) return undefined;
  candidates.sort((a, b) => {
    if (a.statusDate !== b.statusDate) return a.statusDate < b.statusDate ? -1 : 1;
    return (a.version ?? 1) - (b.version ?? 1);
  });
  return candidates[candidates.length - 1];
}

function isCurrentWeekCandidate(scheduled: ScheduledTask, weekStart: ISODate, weekEnd: ISODate, asOf: ISODate): boolean {
  if (scheduled.isSummary || scheduled.isMilestone) return false;
  if (!(scheduled.percentComplete > 0 && scheduled.percentComplete < 100)) return false;
  const effStart = scheduled.start < asOf ? scheduled.start : asOf;
  const effFinish = scheduled.finish > asOf ? scheduled.finish : asOf;
  return effStart <= weekEnd && effFinish >= weekStart;
}

function seedCurrentTaskLines(
  order: readonly string[],
  tasks: Map<string, ScheduledTask>,
  project: Project,
  weekStart: ISODate,
  weekEnd: ISODate,
  asOf: ISODate,
): WeeklyTaskLine[] {
  const candidates: Array<{ id: string; name: string; finish: ISODate; orderIdx: number }> = [];
  order.forEach((id, idx) => {
    const scheduled = tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || !isCurrentWeekCandidate(scheduled, weekStart, weekEnd, asOf)) return;
    candidates.push({ id, name: task.name, finish: scheduled.finish, orderIdx: idx });
  });

  // 마감이 빠른 순 최대 5개를 첫 시드에서 시트에 바로 올린다.
  const byFinish = [...candidates].sort((a, b) => {
    if (a.finish !== b.finish) return a.finish < b.finish ? -1 : 1;
    return a.orderIdx - b.orderIdx;
  });
  const top5Ids = new Set(byFinish.slice(0, INITIAL_CURRENT_TASK_LIMIT).map((c) => c.id));

  return candidates.map((c, idx) => ({
    id: c.id,
    sourceId: c.id,
    text: c.name,
    included: top5Ids.has(c.id),
    order: idx,
    origin: "auto" as const,
    finishDate: c.finish,
  }));
}

function seedNextTaskLines(
  order: readonly string[],
  tasks: Map<string, ScheduledTask>,
  project: Project,
  nextStart: ISODate,
  nextEnd: ISODate,
  excludeTaskIds: Set<string>,
): WeeklyTaskLine[] {
  const lines: WeeklyTaskLine[] = [];
  for (const id of order) {
    if (excludeTaskIds.has(id)) continue;
    const scheduled = tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || scheduled.isSummary || scheduled.isMilestone) continue;
    if (scheduled.percentComplete >= 100) continue;
    if (scheduled.start < nextStart || scheduled.start > nextEnd) continue;
    lines.push({
      id,
      sourceId: id,
      text: task.name,
      included: true,
      order: lines.length,
      origin: "auto",
      finishDate: scheduled.finish,
    });
  }
  return lines;
}

function seedPrevTaskLines(prevReport: WeeklyV2WeekSnapshot | null): WeeklyTaskLine[] {
  if (!prevReport) return [];
  if (Array.isArray(prevReport.currentTaskLines) && prevReport.currentTaskLines.length > 0) {
    return prevReport.currentTaskLines
      .filter((line) => line.included !== false)
      .map((line, idx) => ({
        ...structuredClone(line),
        id: line.id || `prev_${idx}`,
        included: true,
        order: idx,
        origin: "prev" as const,
      }));
  }
  return (prevReport.currentTasks ?? []).map((text, idx) => ({
    id: `prev_${idx}`,
    text,
    included: true,
    order: idx,
    origin: "prev" as const,
  }));
}

function seedAgendaLines(
  order: readonly string[],
  tasks: Map<string, ScheduledTask>,
  project: Project,
  resources: Record<string, UserResource>,
  weekStart: ISODate,
  weekEnd: ISODate,
  prevReport: WeeklyV2WeekSnapshot | null,
): WeeklyAgendaLine[] {
  const from = addDays(weekStart, -7);
  const to = addDays(weekEnd, 14);
  const lines: WeeklyAgendaLine[] = [];
  const seenIds = new Set<string>();

  for (const id of order) {
    const scheduled = tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || !scheduled.isMilestone || scheduled.isSummary) continue;
    if (scheduled.finish < from || scheduled.finish > to) continue;
    const done = scheduled.percentComplete >= 100;
    seenIds.add(id);
    lines.push({
      id,
      sourceId: id,
      date: formatDate(scheduled.finish, "numeric"),
      rawDate: scheduled.finish,
      title: task.name,
      owner: assigneeOf(task, resources),
      type: done ? "완료" : "예정",
      status: done ? "done" : "upcoming",
      included: true,
      order: lines.length,
      origin: "auto",
    });
  }

  // 지난 주 일정 중 아직 완료가 아닌 것은 제안 함에 「지난 주」로 들어온다.
  if (prevReport) {
    const prevAgendas = prevReport.agendaLines ?? prevReport.agendas ?? [];
    for (const item of prevAgendas) {
      const sourceId = item.sourceId ?? item.id;
      if (seenIds.has(sourceId)) continue;
      const scheduled = tasks.get(sourceId);
      const task = taskOf(project, sourceId);
      if (scheduled && task) {
        if (scheduled.percentComplete >= 100) continue;
        seenIds.add(sourceId);
        lines.push({
          id: sourceId,
          sourceId,
          date: formatDate(scheduled.finish, "numeric"),
          rawDate: scheduled.finish,
          title: item.title || task.name,
          owner: assigneeOf(task, resources),
          type: "예정",
          status: "upcoming",
          included: false,
          order: lines.length,
          origin: "prev",
        });
      }
    }
  }

  return lines;
}

/**
 * 기준일(statusDate)의 새 초안을 시드한다.
 */
export function seedWeeklyDraft(
  project: Project,
  resources: Record<string, UserResource>,
  statusDate: ISODate = project.statusDate,
  actorName = "나",
): WeeklyReportDraft {
  const planAtDate: Project = statusDate === project.statusDate ? project : { ...project, statusDate };
  const ctx = buildReportContext(planAtDate, resources);
  const metrics = ctx.metrics;
  const asOf = metrics.statusDate as ISODate;
  const week = weekRangeOf(asOf);
  const nextStart = addDays(week.end, 1);
  const nextEnd = addDays(week.end, 7);
  const prevRange = formatDateRange(addDays(week.start, -7), addDays(week.start, -1), "short");
  const pm = seedPmNames(project, resources);
  const computedVerdict = computeVerdictOf(metrics);
  const statusSummary = computeSummaryOf(metrics);
  const planProgress = metrics.plannedProgress ?? 0;
  const actualProgress = metrics.progress;
  const sections: WeeklySectionVisibility = {
    cost: project.weeklySectionDefaults?.cost ?? DEFAULT_WEEKLY_SECTIONS.cost,
    wbs: project.weeklySectionDefaults?.wbs ?? DEFAULT_WEEKLY_SECTIONS.wbs,
    history: project.weeklySectionDefaults?.history ?? DEFAULT_WEEKLY_SECTIONS.history,
  };

  const prevSnap = findPreviousClosedSnapshot(project, asOf);
  const prevReport = readFrozenReport(prevSnap);

  const prevTasks = seedPrevTaskLines(prevReport);
  const currentTasks = seedCurrentTaskLines(ctx.schedule.order, ctx.schedule.tasks, project, week.start, week.end, asOf);
  const currentTaskSourceIds = new Set(currentTasks.map((t) => t.sourceId).filter((id): id is string => !!id));
  const nextTasks = seedNextTaskLines(ctx.schedule.order, ctx.schedule.tasks, project, nextStart, nextEnd, currentTaskSourceIds);
  const agendas = seedAgendaLines(ctx.schedule.order, ctx.schedule.tasks, project, resources, week.start, week.end, prevReport);
  const issues = seedIssueLines(project, asOf, week.start, week.end);
  const requests = seedRequestLines(project, asOf);
  const wbs = seedWbsLines(project, ctx.schedule.tasks, ctx.schedule.order, ctx.baseline, resources);
  const costEvm = costEvmOf(project, ctx.evm, actualProgress);
  const agingNote = computeAgingNoteFromIssues(issues, asOf);
  const now = new Date().toISOString();

  return {
    id: newId("wdraft"),
    statusDate: asOf,
    version: 1,
    createdAt: now,
    updatedAt: now,
    title: suggestPeriodName(asOf),
    weekLabel: shortWeekLabel(asOf),
    periodLabel: formatDateRange(week.start, week.end, "numeric"),
    asOfDate: formatDate(asOf, "fullWeekday"),
    reportNo: formatReportNo(asOf, 1),
    projectName: project.name,
    projectPeriod: formatDateRange(project.start, ctx.schedule.projectFinish, "full"),
    clientPm: pm.clientPm,
    vendorPm: pm.vendorPm,
    author: actorName,
    sections,
    planProgress,
    actualProgress,
    hasBaseline: metrics.hasBaseline,
    computedVerdict,
    statusVerdict: computedVerdict,
    verdictEdited: false,
    statusSummary,
    statusSummaryEdited: false,
    weekMemo: seedWeekMemo(project.id, asOf, project.statusDate),
    costEvm,
    prevTasksRange: prevReport?.currentTasksRange ?? prevRange,
    currentTasksRange: formatDateRange(week.start, week.end, "short"),
    nextTasksRange: formatDateRange(nextStart, nextEnd, "short"),
    prevTasks,
    currentTasks,
    nextTasks,
    agendas,
    issues,
    requests,
    wbs,
    agingNote,
  };
}

/**
 * 마감본에서 정정본(revision) 초안을 만든다.
 */
export function createRevisionDraftFromSnapshot(
  snapshot: WeeklyReportSnapshot,
  project: Project,
  resources: Record<string, UserResource>,
): WeeklyReportDraft | null {
  const report = readFrozenReport(snapshot);
  if (!report) return null;
  const asOf = snapshot.statusDate;
  const nextVersion = (snapshot.version ?? report.versionNumber ?? 1) + 1;
  const now = new Date().toISOString();
  const baseSeed = seedWeeklyDraft(project, resources, asOf, report.author || "나");

  const prevTasks: WeeklyTaskLine[] = Array.isArray(report.prevTaskLines)
    ? structuredClone(report.prevTaskLines)
    : (report.prevTasks ?? []).map((text, idx) => ({
        id: `prev_${idx}`,
        text,
        included: true,
        order: idx,
        origin: "prev" as const,
      }));

  const currentTasks: WeeklyTaskLine[] = Array.isArray(report.currentTaskLines)
    ? structuredClone(report.currentTaskLines)
    : (report.currentTasks ?? []).map((text, idx) => ({
        id: `cur_${idx}`,
        text,
        included: true,
        order: idx,
        origin: "auto" as const,
      }));

  const nextTasks: WeeklyTaskLine[] = Array.isArray(report.nextTaskLines)
    ? structuredClone(report.nextTaskLines)
    : (report.nextTasks ?? []).map((text, idx) => ({
        id: `next_${idx}`,
        text,
        included: true,
        order: idx,
        origin: "auto" as const,
      }));

  const agendas: WeeklyAgendaLine[] = Array.isArray(report.agendaLines)
    ? structuredClone(report.agendaLines)
    : (report.agendas ?? []).map((item, idx) => ({
        id: item.id,
        sourceId: item.sourceId ?? item.id,
        date: item.date,
        rawDate: item.rawDate,
        title: item.title,
        owner: item.owner,
        type: item.type,
        status: item.status,
        included: true,
        order: idx,
        origin: item.origin ?? "auto",
        carryOver: item.carryOver,
        missingSource: item.missingSource,
      }));

  const issues: WeeklyIssueLine[] = Array.isArray(report.issueLines)
    ? structuredClone(report.issueLines)
    : (report.issues ?? []).map((item, idx) => ({
        id: item.id,
        sourceId: item.sourceId ?? item.id,
        code: item.code,
        date: item.date,
        openedDate: item.openedDate,
        title: item.title,
        severity: item.severity,
        owner: item.owner,
        action: item.action,
        status: item.status,
        included: true,
        order: idx,
        origin: item.origin ?? "auto",
        carryOver: item.carryOver,
        missingSource: item.missingSource,
      }));

  const requests: WeeklyRequestLine[] = Array.isArray(report.requestLines)
    ? structuredClone(report.requestLines)
    : (report.requests ?? []).map((item, idx) => ({
        id: item.id,
        sourceId: item.sourceId ?? item.id,
        code: item.code,
        date: item.date,
        title: item.title,
        neededBy: item.neededBy,
        target: item.target,
        status: item.status,
        impact: item.impact,
        included: true,
        order: idx,
        origin: item.origin ?? "auto",
        carryOver: item.carryOver,
        missingSource: item.missingSource,
      }));

  const wbs: WeeklyWbsLine[] = Array.isArray(report.wbsLines)
    ? structuredClone(report.wbsLines)
    : (report.wbs ?? []).map((row, idx) => ({
        ...structuredClone(row),
        sourceId: row.id,
        included: true,
        order: idx,
      }));

  return {
    ...baseSeed,
    id: newId("wdraft"),
    statusDate: asOf,
    version: nextVersion,
    isRevision: true,
    createdAt: now,
    updatedAt: now,
    title: report.title || report.weekLabel || suggestPeriodName(asOf),
    weekLabel: shortWeekLabel(asOf),
    periodLabel: report.periodLabel,
    asOfDate: report.asOfDate,
    reportNo: formatReportNo(asOf, nextVersion),
    projectName: report.projectName,
    projectPeriod: report.projectPeriod,
    clientPm: report.clientPm,
    vendorPm: report.vendorPm,
    author: report.author || "나",
    sections: report.sections ? { ...report.sections } : { ...DEFAULT_WEEKLY_SECTIONS },
    planProgress: report.planProgress,
    actualProgress: report.actualProgress,
    hasBaseline: report.hasBaseline !== false,
    computedVerdict: report.computedVerdict ?? report.statusVerdict,
    statusVerdict: report.statusVerdict,
    verdictEdited: report.verdictEdited === true,
    statusSummary: report.statusSummary,
    statusSummaryEdited: true,
    weekMemo: report.weekMemo ?? "",
    costEvm: report.costEvm ? structuredClone(report.costEvm) : structuredClone(UNAVAILABLE_COST),
    costNote: report.costNote,
    prevTasksRange: report.prevTasksRange ?? baseSeed.prevTasksRange,
    currentTasksRange: report.currentTasksRange,
    nextTasksRange: report.nextTasksRange,
    prevTasks,
    currentTasks,
    nextTasks,
    agendas,
    issues,
    requests,
    wbs,
    agingNote: report.agingNote ?? computeAgingNoteFromIssues(issues, asOf),
  };
}

/* ------------------------------------------------------------------------------------------------
 * 초안 목록 조작 (포함·제외·순서·문장 편집·수동 추가)
 * ---------------------------------------------------------------------------------------------- */

function touchDraft(draft: WeeklyReportDraft, patch: Partial<WeeklyReportDraft>): WeeklyReportDraft {
  const next = { ...draft, ...patch, updatedAt: new Date().toISOString() };
  if ("issues" in patch && patch.issues) {
    next.agingNote = computeAgingNoteFromIssues(patch.issues, next.statusDate);
  }
  return next;
}

export function includeDraftLine(draft: WeeklyReportDraft, listKey: WeeklyListKey, lineId: string): WeeklyReportDraft {
  const list = draft[listKey] as Array<{ id: string; included: boolean; order: number }>;
  const maxOrder = list.filter((item) => item.included).reduce((max, item) => Math.max(max, item.order), -1);
  const updated = list.map((item) => (item.id === lineId ? { ...item, included: true, order: maxOrder + 1 } : item));
  return touchDraft(draft, { [listKey]: updated } as Partial<WeeklyReportDraft>);
}

export function excludeDraftLine(draft: WeeklyReportDraft, listKey: WeeklyListKey, lineId: string): WeeklyReportDraft {
  const list = draft[listKey] as Array<{ id: string; included: boolean; order: number }>;
  const updated = list.map((item) => (item.id === lineId ? { ...item, included: false } : item));
  return touchDraft(draft, { [listKey]: updated } as Partial<WeeklyReportDraft>);
}

export function reorderDraftLines(draft: WeeklyReportDraft, listKey: WeeklyListKey, orderedIncludedIds: string[]): WeeklyReportDraft {
  const rank = new Map(orderedIncludedIds.map((id, idx) => [id, idx]));
  const list = draft[listKey] as Array<{ id: string; included: boolean; order: number }>;
  const updated = list.map((item) => {
    if (!item.included) return item;
    const nextOrder = rank.get(item.id);
    return nextOrder !== undefined ? { ...item, order: nextOrder } : item;
  });
  const sorted = [...updated].sort((a, b) => {
    if (a.included && b.included) return a.order - b.order;
    if (a.included && !b.included) return -1;
    if (!a.included && b.included) return 1;
    return a.order - b.order;
  });
  return touchDraft(draft, { [listKey]: sorted } as Partial<WeeklyReportDraft>);
}

export function moveDraftLine(draft: WeeklyReportDraft, listKey: WeeklyListKey, lineId: string, direction: -1 | 1): WeeklyReportDraft {
  const list = draft[listKey] as Array<{ id: string; included: boolean; order: number }>;
  const included = [...list.filter((item) => item.included)].sort((a, b) => a.order - b.order);
  const idx = included.findIndex((item) => item.id === lineId);
  if (idx < 0) return draft;
  const targetIdx = idx + direction;
  if (targetIdx < 0 || targetIdx >= included.length) return draft;
  const ids = included.map((item) => item.id);
  const [moved] = ids.splice(idx, 1);
  ids.splice(targetIdx, 0, moved!);
  return reorderDraftLines(draft, listKey, ids);
}

export function updateDraftTaskLine(
  draft: WeeklyReportDraft,
  listKey: "prevTasks" | "currentTasks" | "nextTasks",
  lineId: string,
  patch: Partial<Pick<WeeklyTaskLine, "text" | "carryOver">>,
): WeeklyReportDraft {
  const updated = draft[listKey].map((item) => (item.id === lineId ? { ...item, ...patch } : item));
  return touchDraft(draft, { [listKey]: updated });
}

export function updateDraftAgendaLine(
  draft: WeeklyReportDraft,
  lineId: string,
  patch: Partial<WeeklyAgendaLine>,
): WeeklyReportDraft {
  const updated = draft.agendas.map((item) => (item.id === lineId ? { ...item, ...patch } : item));
  return touchDraft(draft, { agendas: updated });
}

export function updateDraftIssueLine(
  draft: WeeklyReportDraft,
  lineId: string,
  patch: Partial<WeeklyIssueLine>,
): WeeklyReportDraft {
  const updated = draft.issues.map((item) => {
    if (item.id !== lineId) return item;
    const isDirectEdit =
      patch.title !== undefined ||
      patch.action !== undefined ||
      patch.severity !== undefined ||
      patch.status !== undefined ||
      patch.owner !== undefined ||
      patch.date !== undefined ||
      patch.openedDate !== undefined;
    return {
      ...item,
      ...patch,
      ...(isDirectEdit ? { edited: true } : {}),
      ...(patch.edited !== undefined ? { edited: patch.edited } : {}),
    };
  });
  return touchDraft(draft, { issues: updated });
}

export function updateDraftRequestLine(
  draft: WeeklyReportDraft,
  lineId: string,
  patch: Partial<WeeklyRequestLine>,
): WeeklyReportDraft {
  const updated = draft.requests.map((item) => {
    if (item.id !== lineId) return item;
    const isDirectEdit =
      patch.title !== undefined ||
      patch.impact !== undefined ||
      patch.status !== undefined ||
      patch.neededBy !== undefined ||
      patch.target !== undefined ||
      patch.date !== undefined;
    return {
      ...item,
      ...patch,
      ...(isDirectEdit ? { edited: true } : {}),
      ...(patch.edited !== undefined ? { edited: patch.edited } : {}),
    };
  });
  return touchDraft(draft, { requests: updated });
}

export interface AddManualDraftLineInput {
  id?: string;
  insertAfterId?: string;
  text?: string;
  secondary?: string;
  action?: string;
  date?: string;
  openedDate?: ISODate;
  rawDate?: ISODate;
  owner?: string;
  severity?: "High" | "Medium" | "Low" | null;
  status?: string;
  type?: string;
  neededBy?: string;
  target?: string;
  impact?: string;
  allowEmpty?: boolean;
}

export function addManualDraftLine(
  draft: WeeklyReportDraft,
  listKey: Exclude<WeeklyListKey, "wbs">,
  input: AddManualDraftLineInput | { text: string; secondary?: string; [key: string]: any },
): WeeklyReportDraft {
  const id = input.id ?? newId("wline");

  if (listKey === "prevTasks" || listKey === "currentTasks" || listKey === "nextTasks") {
    const primary = input.allowEmpty ? (input.text ?? "") : (input.text ?? "").trim();
    if (!primary && !input.allowEmpty) return draft;
    const list = draft[listKey];
    const targetIdx = input.insertAfterId ? list.findIndex((i) => i.id === input.insertAfterId) : -1;
    const nextLine: WeeklyTaskLine = {
      id,
      text: primary,
      included: true,
      order: 0,
      origin: "manual",
      carryOver: false,
    };
    const updatedList =
      targetIdx >= 0
        ? [...list.slice(0, targetIdx + 1), nextLine, ...list.slice(targetIdx + 1)]
        : [...list, nextLine];
    const reordered = updatedList.map((item, idx) => ({ ...item, order: idx }));
    return touchDraft(draft, { [listKey]: reordered });
  }

  if (listKey === "agendas") {
    const primary = (input.text ?? "").trim() || "새 보고일정";
    const status = (input.status as WeeklyAgendaLine["status"]) || "upcoming";
    const nextLine: WeeklyAgendaLine = {
      id,
      date: input.date ?? formatDate(draft.statusDate, "numeric"),
      rawDate: input.rawDate ?? (isISODate(input.date) ? (input.date as ISODate) : draft.statusDate),
      title: primary,
      owner: input.owner?.trim() || "—",
      type: input.type || (status === "done" ? "완료" : "예정"),
      status,
      included: true,
      order: 0,
      origin: "manual",
      carryOver: false,
    };
    const list = draft.agendas;
    const targetIdx = input.insertAfterId ? list.findIndex((i) => i.id === input.insertAfterId) : -1;
    const updatedList =
      targetIdx >= 0
        ? [...list.slice(0, targetIdx + 1), nextLine, ...list.slice(targetIdx + 1)]
        : [...list, nextLine];
    const reordered = updatedList.map((item, idx) => ({ ...item, order: idx }));
    return touchDraft(draft, { agendas: reordered });
  }

  if (listKey === "issues") {
    const primary = (input.text ?? "").trim() || "새 이슈";
    const secondary = (input.secondary ?? input.action ?? "").trim() || "—";
    const manualCount = draft.issues.filter((i) => !i.sourceId).length + 1;
    const status = (input.status as WeeklyIssueLine["status"]) || "open";
    const nextLine: WeeklyIssueLine = {
      id,
      code: `MAN-${manualCount}`,
      date: input.date ?? formatDate(draft.statusDate, "numeric"),
      openedDate: input.openedDate ?? (isISODate(input.date) ? (input.date as ISODate) : draft.statusDate),
      title: primary,
      severity: input.severity !== undefined ? input.severity : null,
      owner: input.owner?.trim() || "—",
      action: secondary,
      status,
      included: true,
      order: 0,
      origin: "manual",
      carryOver: false,
      edited: true,
    };
    const list = draft.issues;
    const targetIdx = input.insertAfterId ? list.findIndex((i) => i.id === input.insertAfterId) : -1;
    const updatedList =
      targetIdx >= 0
        ? [...list.slice(0, targetIdx + 1), nextLine, ...list.slice(targetIdx + 1)]
        : [...list, nextLine];
    const reordered = updatedList.map((item, idx) => ({ ...item, order: idx }));
    return touchDraft(draft, { issues: reordered });
  }

  const primary = (input.text ?? "").trim() || "새 요청사항";
  const secondary = (input.secondary ?? input.impact ?? "").trim() || "—";
  const manualCount = draft.requests.filter((i) => !i.sourceId).length + 1;
  const status = (input.status as WeeklyRequestLine["status"]) || "pending";
  const nextLine: WeeklyRequestLine = {
    id,
    code: `REQ-${manualCount}`,
    date: input.date ?? formatDate(draft.statusDate, "numeric"),
    title: primary,
    neededBy: input.neededBy?.trim() || "—",
    target: input.target?.trim() || "—",
    status,
    impact: secondary,
    included: true,
    order: 0,
    origin: "manual",
    carryOver: false,
    edited: true,
  };
  const list = draft.requests;
  const targetIdx = input.insertAfterId ? list.findIndex((i) => i.id === input.insertAfterId) : -1;
  const updatedList =
    targetIdx >= 0
      ? [...list.slice(0, targetIdx + 1), nextLine, ...list.slice(targetIdx + 1)]
      : [...list, nextLine];
  const reordered = updatedList.map((item, idx) => ({ ...item, order: idx }));
  return touchDraft(draft, { requests: reordered });
}

export function deleteDraftLine(
  draft: WeeklyReportDraft,
  listKey: WeeklyListKey,
  lineId: string,
): WeeklyReportDraft {
  const list = draft[listKey] as Array<{ id: string; order: number }>;
  const updated = list.filter((item) => item.id !== lineId);
  return touchDraft(draft, { [listKey]: updated } as Partial<WeeklyReportDraft>);
}

export function promoteManualIssueToRecord(
  draft: WeeklyReportDraft,
  lineId: string,
  recordOrId: string | { id: string; code?: string },
  code?: string,
): WeeklyReportDraft {
  const recordId = typeof recordOrId === "string" ? recordOrId : recordOrId.id;
  const finalCode =
    typeof recordOrId === "string"
      ? (code ?? `ISS-${recordId.slice(-2)}`)
      : (recordOrId.code ?? code ?? `ISS-${recordId.slice(-2)}`);
  const updated = draft.issues.map((item) => {
    if (item.id !== lineId) return item;
    return {
      ...item,
      sourceId: recordId,
      code: finalCode,
      origin: "auto" as const,
    };
  });
  return touchDraft(draft, { issues: updated });
}

/* ------------------------------------------------------------------------------------------------
 * 다시 읽기 (공정 / 일정 / 기록 / 단일 줄)
 * ---------------------------------------------------------------------------------------------- */

/**
 * 「공정 다시 읽기」: 초안의 공정 숫자와 계산 평가를 현재 일정·원가 값으로 바꾼다.
 * 작성자가 적어 둔 설명 문장(statusSummary)과 비용 메모(costNote)는 그대로 둔다.
 */
export function reloadDraftProgress(
  draft: WeeklyReportDraft,
  project: Project,
  resources: Record<string, UserResource>,
): WeeklyReportDraft {
  const planAtDate: Project = draft.statusDate === project.statusDate ? project : { ...project, statusDate: draft.statusDate };
  const ctx = buildReportContext(planAtDate, resources);
  const metrics = ctx.metrics;
  const planProgress = metrics.plannedProgress ?? 0;
  const actualProgress = metrics.progress;
  const computedVerdict = computeVerdictOf(metrics);
  const statusVerdict = draft.verdictEdited ? draft.statusVerdict : computedVerdict;
  const costEvm = costEvmOf(project, ctx.evm, actualProgress);

  return touchDraft(draft, {
    planProgress,
    actualProgress,
    hasBaseline: metrics.hasBaseline,
    computedVerdict,
    statusVerdict,
    costEvm,
    projectName: project.name,
    projectPeriod: formatDateRange(project.start, ctx.schedule.projectFinish, "full"),
  });
}

/**
 * 「일정에서 다시 읽기」: 금주·차주·일정·WBS를 현재 일정과 대조해 갱신한다.
 * - 이미 있는 줄의 날짜, 상태, 담당, 공정 숫자는 바꾼다. 작성 문장(text, title)은 유지한다.
 * - 새 원본은 제안 함에 「새로 생김」(origin: "reloaded", included: false)으로 들어온다.
 * - 원본이 사라진 줄은 시트에 남고, 줄 옆에 「원본 없음」(missingSource: true)을 붙인다.
 */
export function reloadDraftSchedule(
  draft: WeeklyReportDraft,
  project: Project,
  resources: Record<string, UserResource>,
): WeeklyReportDraft {
  const asOf = draft.statusDate;
  const planAtDate: Project = asOf === project.statusDate ? project : { ...project, statusDate: asOf };
  const ctx = buildReportContext(planAtDate, resources);
  const week = weekRangeOf(asOf);
  const nextStart = addDays(week.end, 1);
  const nextEnd = addDays(week.end, 7);

  // 1. 금주 (currentTasks)
  const existingCurSourceIds = new Set<string>();
  const updatedCurrentTasks = draft.currentTasks.map((line) => {
    if (!line.sourceId) return line;
    existingCurSourceIds.add(line.sourceId);
    const task = taskOf(project, line.sourceId);
    const scheduled = ctx.schedule.tasks.get(line.sourceId);
    if (!task || !scheduled) {
      return { ...line, missingSource: true };
    }
    return {
      ...line,
      finishDate: scheduled.finish,
      missingSource: false,
    };
  });

  for (const id of ctx.schedule.order) {
    if (existingCurSourceIds.has(id)) continue;
    const scheduled = ctx.schedule.tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || !isCurrentWeekCandidate(scheduled, week.start, week.end, asOf)) continue;
    existingCurSourceIds.add(id);
    updatedCurrentTasks.push({
      id,
      sourceId: id,
      text: task.name,
      included: false,
      order: updatedCurrentTasks.length,
      origin: "reloaded",
      finishDate: scheduled.finish,
    });
  }

  // 2. 차주 (nextTasks)
  const existingNextSourceIds = new Set<string>();
  const updatedNextTasks = draft.nextTasks.map((line) => {
    if (!line.sourceId) return line;
    existingNextSourceIds.add(line.sourceId);
    const task = taskOf(project, line.sourceId);
    const scheduled = ctx.schedule.tasks.get(line.sourceId);
    if (!task || !scheduled) {
      return { ...line, missingSource: true };
    }
    return {
      ...line,
      finishDate: scheduled.finish,
      missingSource: false,
    };
  });

  for (const id of ctx.schedule.order) {
    if (existingNextSourceIds.has(id) || existingCurSourceIds.has(id)) continue;
    const scheduled = ctx.schedule.tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || scheduled.isSummary || scheduled.isMilestone) continue;
    if (scheduled.percentComplete >= 100 || scheduled.start < nextStart || scheduled.start > nextEnd) continue;
    existingNextSourceIds.add(id);
    updatedNextTasks.push({
      id,
      sourceId: id,
      text: task.name,
      included: false,
      order: updatedNextTasks.length,
      origin: "reloaded",
      finishDate: scheduled.finish,
    });
  }

  // 3. 지난주 (prevTasks) — 원본이 있었는데 사라졌으면 missingSource 표시
  const updatedPrevTasks = draft.prevTasks.map((line) => {
    if (!line.sourceId) return line;
    const task = taskOf(project, line.sourceId);
    return task ? { ...line, missingSource: false } : { ...line, missingSource: true };
  });

  // 4. 주요 보고일정 (agendas)
  const existingAgendaSourceIds = new Set<string>();
  const updatedAgendas = draft.agendas.map((line) => {
    if (!line.sourceId) return line;
    existingAgendaSourceIds.add(line.sourceId);
    const task = taskOf(project, line.sourceId);
    const scheduled = ctx.schedule.tasks.get(line.sourceId);
    if (!task || !scheduled) {
      return { ...line, missingSource: true };
    }
    const done = scheduled.percentComplete >= 100;
    return {
      ...line,
      date: formatDate(scheduled.finish, "numeric"),
      rawDate: scheduled.finish,
      owner: assigneeOf(task, resources),
      type: done ? "완료" : "예정",
      status: done ? ("done" as const) : ("upcoming" as const),
      missingSource: false,
    };
  });

  const from = addDays(week.start, -7);
  const to = addDays(week.end, 14);
  for (const id of ctx.schedule.order) {
    if (existingAgendaSourceIds.has(id)) continue;
    const scheduled = ctx.schedule.tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || !scheduled.isMilestone || scheduled.isSummary) continue;
    if (scheduled.finish < from || scheduled.finish > to) continue;
    const done = scheduled.percentComplete >= 100;
    existingAgendaSourceIds.add(id);
    updatedAgendas.push({
      id,
      sourceId: id,
      date: formatDate(scheduled.finish, "numeric"),
      rawDate: scheduled.finish,
      title: task.name,
      owner: assigneeOf(task, resources),
      type: done ? "완료" : "예정",
      status: done ? "done" : "upcoming",
      included: false,
      order: updatedAgendas.length,
      origin: "reloaded",
    });
  }

  // 5. WBS (업무 진행상황)
  const existingWbsIds = new Set<string>();
  const updatedWbs: WeeklyWbsLine[] = draft.wbs.map((row) => {
    const sid = row.sourceId ?? row.id;
    existingWbsIds.add(sid);
    const task = taskOf(project, sid);
    const scheduled = ctx.schedule.tasks.get(sid);
    if (!task || !scheduled) {
      return { ...row, missingSource: true };
    }
    const fresh = buildWbsLine(project, scheduled, task, ctx.baseline, resources, row.order, row.included);
    return { ...fresh, id: row.id, sourceId: sid, included: row.included, order: row.order, missingSource: false };
  });

  for (const id of ctx.schedule.order) {
    if (existingWbsIds.has(id)) continue;
    const scheduled = ctx.schedule.tasks.get(id);
    const task = taskOf(project, id);
    if (!scheduled || !task || scheduled.depth > 1) continue;
    existingWbsIds.add(id);
    updatedWbs.push(buildWbsLine(project, scheduled, task, ctx.baseline, resources, updatedWbs.length, false));
  }

  return touchDraft(draft, {
    currentTasksRange: formatDateRange(week.start, week.end, "short"),
    nextTasksRange: formatDateRange(nextStart, nextEnd, "short"),
    prevTasks: updatedPrevTasks,
    currentTasks: updatedCurrentTasks,
    nextTasks: updatedNextTasks,
    agendas: updatedAgendas,
    wbs: updatedWbs,
  });
}

/**
 * 「기록에서 다시 읽기」: 이슈·리스크와 요청사항을 현재 기록과 대조해 갱신한다.
 * - 이미 있는 줄의 날짜, 상태, 담당, 심각도는 바꾼다. 작성 문장(금주 조치내용 action, 영향 문장 impact)은 유지한다.
 * - 새 원본은 제안 함에 「새로 생김」(origin: "reloaded", included: false)으로 들어온다.
 * - 원본이 사라진 줄은 시트에 남고, 줄 옆에 「원본 없음」(missingSource: true)을 붙인다.
 */
export function reloadDraftRecords(draft: WeeklyReportDraft, project: Project): WeeklyReportDraft {
  const asOf = draft.statusDate;
  const week = weekRangeOf(asOf);
  const recordMap = new Map((project.records ?? []).map((r) => [r.id, r]));

  // 1. 이슈·리스크
  const existingIssueSourceIds = new Set<string>();
  const updatedIssues = draft.issues.map((line) => {
    if (!line.sourceId) return line;
    existingIssueSourceIds.add(line.sourceId);
    const record = recordMap.get(line.sourceId);
    if (!record) {
      return { ...line, missingSource: true };
    }
    const opened = dayOf(record.createdAt) ?? asOf;
    return {
      ...line,
      code: recordCode(record),
      date: formatDate(opened, "numeric"),
      openedDate: opened,
      title: record.title || line.title,
      severity: issueSeverityOf(record),
      owner: record.assignees.filter(Boolean).join(", ") || "—",
      status: issueStatusOf(record, asOf),
      // 작성 문장(조치)은 유지한다
      action: line.action,
      missingSource: false,
    };
  });

  for (const record of project.records ?? []) {
    if (existingIssueSourceIds.has(record.id)) continue;
    if (record.kind !== "issue" && record.kind !== "risk") continue;
    const opened = dayOf(record.createdAt);
    if (!opened || opened > asOf) continue;
    if (recordPhase(record.status) === "closed" && !inWeekClosed(record, week.start, week.end)) continue;
    existingIssueSourceIds.add(record.id);
    updatedIssues.push(buildIssueLineFromRecord(record, asOf, updatedIssues.length, false, "reloaded"));
  }

  // 2. 요청사항
  const existingReqSourceIds = new Set<string>();
  const updatedRequests = draft.requests.map((line) => {
    if (!line.sourceId) return line;
    existingReqSourceIds.add(line.sourceId);
    const record = recordMap.get(line.sourceId);
    if (!record) {
      return { ...line, missingSource: true };
    }
    const opened = dayOf(record.createdAt) ?? asOf;
    const closed =
      record.kind === "change"
        ? recordPhase(record.status) === "closed"
        : record.status === "accepted" || record.status === "rejected" || record.status === "superseded";
    return {
      ...line,
      code: recordCode(record),
      date: formatDate(opened, "numeric"),
      title: record.title || line.title,
      neededBy: record.due ? formatDate(record.due, "numeric") : "—",
      target: record.assignees.filter(Boolean).join(", ") || "—",
      status: closed ? ("approved" as const) : requestStatusOf(record, asOf),
      // 작성 문장(영향)은 유지한다
      impact: line.impact,
      missingSource: false,
    };
  });

  for (const record of project.records ?? []) {
    if (existingReqSourceIds.has(record.id)) continue;
    if (!isOpenRequestRecord(record, asOf)) continue;
    existingReqSourceIds.add(record.id);
    updatedRequests.push(buildRequestLineFromRecord(record, asOf, updatedRequests.length, false, "reloaded"));
  }

  return touchDraft(draft, {
    issues: updatedIssues,
    requests: updatedRequests,
  });
}

/**
 * 「이 줄만 다시 읽기」: 한 줄만 원본(작업 또는 기록)에서 다시 읽는다.
 * 작성 문장(금주 한 줄, 조치, 영향)은 유지하고, 원본이 사라졌으면 missingSource: true로 표시한다.
 */
export function reloadSingleDraftLine(
  draft: WeeklyReportDraft,
  listKey: WeeklyListKey,
  lineId: string,
  project: Project,
  resources: Record<string, UserResource>,
): WeeklyReportDraft {
  const asOf = draft.statusDate;
  const planAtDate: Project = asOf === project.statusDate ? project : { ...project, statusDate: asOf };
  const ctx = buildReportContext(planAtDate, resources);

  if (listKey === "prevTasks" || listKey === "currentTasks" || listKey === "nextTasks") {
    const updated = draft[listKey].map((line) => {
      if (line.id !== lineId || !line.sourceId) return line;
      const task = taskOf(project, line.sourceId);
      const scheduled = ctx.schedule.tasks.get(line.sourceId);
      if (!task || !scheduled) return { ...line, missingSource: true };
      return { ...line, finishDate: scheduled.finish, missingSource: false };
    });
    return touchDraft(draft, { [listKey]: updated });
  }

  if (listKey === "agendas") {
    const updated = draft.agendas.map((line) => {
      if (line.id !== lineId || !line.sourceId) return line;
      const task = taskOf(project, line.sourceId);
      const scheduled = ctx.schedule.tasks.get(line.sourceId);
      if (!task || !scheduled) return { ...line, missingSource: true };
      const done = scheduled.percentComplete >= 100;
      return {
        ...line,
        date: formatDate(scheduled.finish, "numeric"),
        rawDate: scheduled.finish,
        owner: assigneeOf(task, resources),
        type: done ? "완료" : "예정",
        status: done ? ("done" as const) : ("upcoming" as const),
        missingSource: false,
      };
    });
    return touchDraft(draft, { agendas: updated });
  }

  if (listKey === "issues") {
    const updated = draft.issues.map((line) => {
      if (line.id !== lineId || !line.sourceId) return line;
      const record = (project.records ?? []).find((r) => r.id === line.sourceId);
      if (!record) return { ...line, missingSource: true };
      const opened = dayOf(record.createdAt) ?? asOf;
      return {
        ...line,
        code: recordCode(record),
        date: formatDate(opened, "numeric"),
        openedDate: opened,
        title: record.title || line.title,
        severity: issueSeverityOf(record),
        owner: record.assignees.filter(Boolean).join(", ") || "—",
        status: issueStatusOf(record, asOf),
        missingSource: false,
      };
    });
    return touchDraft(draft, { issues: updated });
  }

  if (listKey === "requests") {
    const updated = draft.requests.map((line) => {
      if (line.id !== lineId || !line.sourceId) return line;
      const record = (project.records ?? []).find((r) => r.id === line.sourceId);
      if (!record) return { ...line, missingSource: true };
      const opened = dayOf(record.createdAt) ?? asOf;
      const closed =
        record.kind === "change"
          ? recordPhase(record.status) === "closed"
          : record.status === "accepted" || record.status === "rejected" || record.status === "superseded";
      return {
        ...line,
        code: recordCode(record),
        date: formatDate(opened, "numeric"),
        title: record.title || line.title,
        neededBy: record.due ? formatDate(record.due, "numeric") : "—",
        target: record.assignees.filter(Boolean).join(", ") || "—",
        status: closed ? ("approved" as const) : requestStatusOf(record, asOf),
        missingSource: false,
      };
    });
    return touchDraft(draft, { requests: updated });
  }

  if (listKey === "wbs") {
    const updated = draft.wbs.map((row) => {
      if (row.id !== lineId) return row;
      const sid = row.sourceId ?? row.id;
      const task = taskOf(project, sid);
      const scheduled = ctx.schedule.tasks.get(sid);
      if (!task || !scheduled) return { ...row, missingSource: true };
      const fresh = buildWbsLine(project, scheduled, task, ctx.baseline, resources, row.order, row.included);
      return { ...fresh, id: row.id, sourceId: sid, included: row.included, order: row.order, missingSource: false };
    });
    return touchDraft(draft, { wbs: updated });
  }

  return draft;
}

/* ------------------------------------------------------------------------------------------------
 * 지난 보고의 미결 가져오기 & 지난 항목 검색
 * ---------------------------------------------------------------------------------------------- */

export interface CarryOverPack {
  prevStatusDate: ISODate;
  prevWeekLabel: string;
  currentTasks: WeeklyTaskLine[];
  agendas: WeeklyAgendaLine[];
  issues: WeeklyIssueLine[];
  requests: WeeklyRequestLine[];
  prevTasks: WeeklyTaskLine[];
  nextTasks: WeeklyTaskLine[];
}

/**
 * 바로 이전 마감본에 포함됐던 줄 가운데 아직 닫히지 않은(미결) 후보를 모은다.
 * 이미 닫힌 원본은 포함하지 않는다.
 */
export function collectCarryOverCandidates(
  project: Project,
  resources: Record<string, UserResource>,
  statusDate: ISODate = project.statusDate,
): CarryOverPack | null {
  const prevSnap = findPreviousClosedSnapshot(project, statusDate);
  const prevReport = readFrozenReport(prevSnap);
  if (!prevSnap || !prevReport) return null;

  const planAtDate: Project = statusDate === project.statusDate ? project : { ...project, statusDate };
  const ctx = buildReportContext(planAtDate, resources);
  const recordMap = new Map((project.records ?? []).map((r) => [r.id, r]));

  // 1. 금주: 이전 문서의 차주 줄 가운데 작업이 아직 끝나지 않은 것 + 이전 금주의 다음 주로 넘김 수동 줄
  const currentTasks: WeeklyTaskLine[] = [];
  const prevNextLines: WeeklyTaskLine[] = Array.isArray(prevReport.nextTaskLines)
    ? prevReport.nextTaskLines.filter((l) => l.included !== false)
    : (prevReport.nextTasks ?? []).map((text, idx) => {
        const matchedTask = project.tasks.find((t) => t.name === text);
        return {
          id: matchedTask?.id ?? `prev_next_${idx}`,
          sourceId: matchedTask?.id,
          text,
          included: true,
          order: idx,
          origin: "prev" as const,
        };
      });

  for (const line of prevNextLines) {
    if (line.sourceId) {
      const task = taskOf(project, line.sourceId);
      const scheduled = ctx.schedule.tasks.get(line.sourceId);
      if (!task || !scheduled || scheduled.percentComplete >= 100) continue;
      currentTasks.push({
        id: line.sourceId,
        sourceId: line.sourceId,
        text: line.text || task.name,
        included: false,
        order: currentTasks.length,
        origin: "prev",
        finishDate: scheduled.finish,
      });
    } else if (line.carryOver) {
      currentTasks.push({
        ...structuredClone(line),
        id: newId("wline"),
        included: false,
        order: currentTasks.length,
        origin: "prev",
        carryOver: false,
      });
    }
  }

  const prevCurManual = (prevReport.currentTaskLines ?? []).filter((l) => l.included !== false && !l.sourceId && l.carryOver);
  for (const line of prevCurManual) {
    currentTasks.push({
      ...structuredClone(line),
      id: newId("wline"),
      included: false,
      order: currentTasks.length,
      origin: "prev",
      carryOver: false,
    });
  }

  // 2. 일정: 이전 문서의 일정 가운데 아직 완료가 아닌 것 + 다음 주로 넘김 수동 줄
  const agendas: WeeklyAgendaLine[] = [];
  const prevAgendas = prevReport.agendaLines ?? prevReport.agendas ?? [];
  for (const item of prevAgendas) {
    const sid = item.sourceId ?? (taskOf(project, item.id) ? item.id : undefined);
    if (sid) {
      const task = taskOf(project, sid);
      const scheduled = ctx.schedule.tasks.get(sid);
      if (!task || !scheduled || scheduled.percentComplete >= 100) continue;
      agendas.push({
        id: sid,
        sourceId: sid,
        date: formatDate(scheduled.finish, "numeric"),
        rawDate: scheduled.finish,
        title: item.title || task.name,
        owner: assigneeOf(task, resources),
        type: "예정",
        status: "upcoming",
        included: false,
        order: agendas.length,
        origin: "prev",
      });
    } else if (item.carryOver) {
      agendas.push({
        id: newId("wline"),
        date: item.date,
        rawDate: item.rawDate,
        title: item.title,
        owner: item.owner,
        type: item.type,
        status: item.status,
        included: false,
        order: agendas.length,
        origin: "prev",
        carryOver: false,
      });
    }
  }

  // 3. 이슈·리스크: 이전 문서에 포함됐고 원본이 아직 닫히지 않은 줄 + 다음 주로 넘김 수동 줄
  const issues: WeeklyIssueLine[] = [];
  const prevIssues = prevReport.issueLines ?? prevReport.issues ?? [];
  for (const item of prevIssues) {
    const sid = item.sourceId ?? (recordMap.has(item.id) ? item.id : undefined);
    if (sid) {
      const record = recordMap.get(sid);
      if (!record || recordPhase(record.status) === "closed") continue;
      const opened = dayOf(record.createdAt) ?? statusDate;
      issues.push({
        id: sid,
        sourceId: sid,
        code: recordCode(record),
        date: formatDate(opened, "numeric"),
        openedDate: opened,
        title: record.title || item.title,
        severity: issueSeverityOf(record),
        owner: record.assignees.filter(Boolean).join(", ") || "—",
        // 조치 문장은 이전 문장을 시작값으로 복사
        action: item.action,
        status: issueStatusOf(record, statusDate),
        included: false,
        order: issues.length,
        origin: "prev",
      });
    } else if (item.carryOver) {
      issues.push({
        id: newId("wline"),
        code: item.code,
        date: item.date,
        openedDate: item.openedDate,
        title: item.title,
        severity: item.severity,
        owner: item.owner,
        action: item.action,
        status: item.status,
        included: false,
        order: issues.length,
        origin: "prev",
        carryOver: false,
      });
    }
  }

  // 4. 요청: 이전 문서에 포함됐고 원본이 아직 닫히지 않은 줄 + 다음 주로 넘김 수동 줄
  const requests: WeeklyRequestLine[] = [];
  const prevRequests = prevReport.requestLines ?? prevReport.requests ?? [];
  for (const item of prevRequests) {
    const sid = item.sourceId ?? (recordMap.has(item.id) ? item.id : undefined);
    if (sid) {
      const record = recordMap.get(sid);
      if (!record || !isOpenRequestRecord(record, statusDate)) continue;
      const opened = dayOf(record.createdAt) ?? statusDate;
      requests.push({
        id: sid,
        sourceId: sid,
        code: recordCode(record),
        date: formatDate(opened, "numeric"),
        title: record.title || item.title,
        neededBy: record.due ? formatDate(record.due, "numeric") : "—",
        target: record.assignees.filter(Boolean).join(", ") || "—",
        status: requestStatusOf(record, statusDate),
        // 영향 문장은 이전 문장을 시작값으로 복사
        impact: item.impact,
        included: false,
        order: requests.length,
        origin: "prev",
      });
    } else if (item.carryOver) {
      requests.push({
        id: newId("wline"),
        code: item.code,
        date: item.date,
        title: item.title,
        neededBy: item.neededBy,
        target: item.target,
        status: item.status,
        impact: item.impact,
        included: false,
        order: requests.length,
        origin: "prev",
        carryOver: false,
      });
    }
  }

  // 5. 지난주/차주 수동 줄 중 다음 주로 넘김 켜진 것
  const prevTasks: WeeklyTaskLine[] = (prevReport.prevTaskLines ?? [])
    .filter((l) => l.included !== false && !l.sourceId && l.carryOver)
    .map((l, idx) => ({
      ...structuredClone(l),
      id: newId("wline"),
      included: false,
      order: idx,
      origin: "prev" as const,
      carryOver: false,
    }));

  const nextTasks: WeeklyTaskLine[] = (prevReport.nextTaskLines ?? [])
    .filter((l) => l.included !== false && !l.sourceId && l.carryOver)
    .map((l, idx) => ({
      ...structuredClone(l),
      id: newId("wline"),
      included: false,
      order: idx,
      origin: "prev" as const,
      carryOver: false,
    }));

  return {
    prevStatusDate: prevSnap.statusDate,
    prevWeekLabel: shortWeekLabel(prevSnap.statusDate),
    currentTasks,
    agendas,
    issues,
    requests,
    prevTasks,
    nextTasks,
  };
}

/**
 * 「지난 보고의 미결 가져오기」를 초안의 특정 목록(또는 전체)에 적용한다.
 * - 이슈·요청: 이전 문서에 포함됐고 원본이 아직 닫히지 않은 줄. 조치/영향 문장은 이전 문장을 시작값으로 복사하고 출처를 「지난 주」로 둔다.
 *   이미 같은 원본이 있으면 문장을 덮지 않고 제안에도 중복을 만들지 않는다.
 * - 금주: 이전 문서의 차주 줄 가운데 작업이 아직 끝나지 않은 것. 자동 금주와 같은 작업이면 한 줄로 합치고, 작성 문장이 있으면 그 문장을 유지한다.
 * - 일정: 이전 문서의 일정 가운데 아직 완료가 아닌 것.
 * - 직접 입력 줄: 이전 문서에 「다음 주로 넘김」이 켜진 것만.
 */
export function applyCarryOverToDraft(
  draft: WeeklyReportDraft,
  project: Project,
  resources: Record<string, UserResource>,
  targetKey?: Exclude<WeeklyListKey, "wbs">,
): WeeklyReportDraft {
  const pack = collectCarryOverCandidates(project, resources, draft.statusDate);
  if (!pack) return draft;

  let next = { ...draft };

  const shouldApply = (k: Exclude<WeeklyListKey, "wbs">) => !targetKey || targetKey === k;

  if (shouldApply("currentTasks")) {
    const merged = [...next.currentTasks];
    for (const cand of pack.currentTasks) {
      if (cand.sourceId) {
        const existingIdx = merged.findIndex((item) => item.sourceId === cand.sourceId);
        if (existingIdx >= 0) {
          const existing = merged[existingIdx]!;
          const task = taskOf(project, cand.sourceId);
          const existingIsDefault = task ? existing.text === task.name : false;
          const candHasCustom = task ? cand.text !== task.name : false;
          merged[existingIdx] = {
            ...existing,
            origin: "prev",
            text: !existingIsDefault ? existing.text : candHasCustom ? cand.text : existing.text,
          };
        } else {
          merged.push({ ...cand, included: false, order: merged.length });
        }
      } else {
        if (!merged.some((item) => !item.sourceId && item.text === cand.text)) {
          merged.push({ ...cand, included: false, order: merged.length });
        }
      }
    }
    next = { ...next, currentTasks: merged };
  }

  if (shouldApply("agendas")) {
    const merged = [...next.agendas];
    for (const cand of pack.agendas) {
      if (cand.sourceId) {
        if (!merged.some((item) => item.sourceId === cand.sourceId)) {
          merged.push({ ...cand, included: false, order: merged.length });
        }
      } else if (!merged.some((item) => !item.sourceId && item.title === cand.title)) {
        merged.push({ ...cand, included: false, order: merged.length });
      }
    }
    next = { ...next, agendas: merged };
  }

  if (shouldApply("issues")) {
    const merged = [...next.issues];
    for (const cand of pack.issues) {
      if (cand.sourceId) {
        const existingIdx = merged.findIndex((item) => item.sourceId === cand.sourceId);
        if (existingIdx < 0) {
          merged.push({ ...cand, included: false, order: merged.length });
        }
        // 이미 같은 원본이 있으면 문장을 덮지 않고 제안에도 중복을 만들지 않는다.
      } else if (!merged.some((item) => !item.sourceId && item.title === cand.title)) {
        merged.push({ ...cand, included: false, order: merged.length });
      }
    }
    next = { ...next, issues: merged };
  }

  if (shouldApply("requests")) {
    const merged = [...next.requests];
    for (const cand of pack.requests) {
      if (cand.sourceId) {
        const existingIdx = merged.findIndex((item) => item.sourceId === cand.sourceId);
        if (existingIdx < 0) {
          merged.push({ ...cand, included: false, order: merged.length });
        }
      } else if (!merged.some((item) => !item.sourceId && item.title === cand.title)) {
        merged.push({ ...cand, included: false, order: merged.length });
      }
    }
    next = { ...next, requests: merged };
  }

  if (shouldApply("prevTasks")) {
    const merged = [...next.prevTasks];
    for (const cand of pack.prevTasks) {
      if (!merged.some((item) => !item.sourceId && item.text === cand.text)) {
        merged.push({ ...cand, included: false, order: merged.length });
      }
    }
    next = { ...next, prevTasks: merged };
  }

  if (shouldApply("nextTasks")) {
    const merged = [...next.nextTasks];
    for (const cand of pack.nextTasks) {
      if (!merged.some((item) => !item.sourceId && item.text === cand.text)) {
        merged.push({ ...cand, included: false, order: merged.length });
      }
    }
    next = { ...next, nextTasks: merged };
  }

  return touchDraft(draft, next);
}

export interface PastWeeklyItemEntry {
  key: string;
  listKey: Exclude<WeeklyListKey, "wbs">;
  statusDate: ISODate;
  weekLabel: string;
  sourceId?: string;
  primaryText: string;
  secondaryText?: string;
  raw: WeeklyTaskLine | WeeklyAgendaLine | WeeklyIssueLine | WeeklyRequestLine;
}

/**
 * 「지난 항목」: 이 플랜의 마감본에 한 번이라도 올라간 줄의 목록을 모은다.
 */
export function collectPastItems(
  project: Pick<Project, "weeklySnapshots">,
  listKey: Exclude<WeeklyListKey, "wbs">,
): PastWeeklyItemEntry[] {
  const byDate = new Map<ISODate, WeeklyReportSnapshot>();
  for (const snap of project.weeklySnapshots ?? []) {
    const prev = byDate.get(snap.statusDate);
    if (!prev || (snap.version ?? 1) >= (prev.version ?? 1)) {
      byDate.set(snap.statusDate, snap);
    }
  }
  const snapshots = [...byDate.values()].sort((a, b) => (a.statusDate > b.statusDate ? -1 : 1));
  const out: PastWeeklyItemEntry[] = [];

  for (const snap of snapshots) {
    const report = readFrozenReport(snap);
    if (!report) continue;
    const weekLabel = shortWeekLabel(snap.statusDate);

    if (listKey === "prevTasks" || listKey === "currentTasks" || listKey === "nextTasks") {
      const lines: WeeklyTaskLine[] =
        listKey === "prevTasks"
          ? (report.prevTaskLines ?? (report.prevTasks ?? []).map((t, i) => ({ id: `p_${i}`, text: t, included: true, order: i, origin: "prev" as const })))
          : listKey === "currentTasks"
            ? (report.currentTaskLines ?? (report.currentTasks ?? []).map((t, i) => ({ id: `c_${i}`, text: t, included: true, order: i, origin: "auto" as const })))
            : (report.nextTaskLines ?? (report.nextTasks ?? []).map((t, i) => ({ id: `n_${i}`, text: t, included: true, order: i, origin: "auto" as const })));
      for (const line of lines) {
        if (line.included === false) continue;
        out.push({
          key: `${snap.id}:${listKey}:${line.id}:${line.text}`,
          listKey,
          statusDate: snap.statusDate,
          weekLabel,
          sourceId: line.sourceId,
          primaryText: line.text,
          raw: line,
        });
      }
    } else if (listKey === "agendas") {
      const lines = report.agendaLines ?? report.agendas ?? [];
      for (const item of lines) {
        out.push({
          key: `${snap.id}:agendas:${item.id}:${item.title}`,
          listKey,
          statusDate: snap.statusDate,
          weekLabel,
          sourceId: item.sourceId ?? item.id,
          primaryText: item.title,
          secondaryText: `${item.date} · ${item.type}`,
          raw: {
            id: item.id,
            sourceId: item.sourceId ?? item.id,
            date: item.date,
            rawDate: item.rawDate,
            title: item.title,
            owner: item.owner,
            type: item.type,
            status: item.status,
            included: true,
            order: 0,
            origin: "prev",
          },
        });
      }
    } else if (listKey === "issues") {
      const lines = report.issueLines ?? report.issues ?? [];
      for (const item of lines) {
        out.push({
          key: `${snap.id}:issues:${item.id}:${item.title}`,
          listKey,
          statusDate: snap.statusDate,
          weekLabel,
          sourceId: item.sourceId ?? item.id,
          primaryText: item.title,
          secondaryText: item.action,
          raw: {
            id: item.id,
            sourceId: item.sourceId ?? item.id,
            code: item.code,
            date: item.date,
            openedDate: item.openedDate,
            title: item.title,
            severity: item.severity,
            owner: item.owner,
            action: item.action,
            status: item.status,
            included: true,
            order: 0,
            origin: "prev",
          },
        });
      }
    } else if (listKey === "requests") {
      const lines = report.requestLines ?? report.requests ?? [];
      for (const item of lines) {
        out.push({
          key: `${snap.id}:requests:${item.id}:${item.title}`,
          listKey,
          statusDate: snap.statusDate,
          weekLabel,
          sourceId: item.sourceId ?? item.id,
          primaryText: item.title,
          secondaryText: item.impact,
          raw: {
            id: item.id,
            sourceId: item.sourceId ?? item.id,
            code: item.code,
            date: item.date,
            title: item.title,
            neededBy: item.neededBy,
            target: item.target,
            status: item.status,
            impact: item.impact,
            included: true,
            order: 0,
            origin: "prev",
          },
        });
      }
    }
  }

  return out;
}

/**
 * 「지난 항목」에서 고른 줄을 이번 초안에 새 줄로 복사한다.
 * 문장도 그때 문장으로 복사하고, 원본이 아직 있으면 원본 id를 붙이고, 없으면 수동 줄로 복사한다.
 */
export function copyPastItemToDraft(
  draft: WeeklyReportDraft,
  project: Project,
  entry: PastWeeklyItemEntry,
): WeeklyReportDraft {
  const { listKey, raw } = entry;
  const newLineId = newId("wpast");

  if (listKey === "prevTasks" || listKey === "currentTasks" || listKey === "nextTasks") {
    const taskLine = raw as WeeklyTaskLine;
    const aliveTask = taskLine.sourceId ? taskOf(project, taskLine.sourceId) : undefined;
    const list = draft[listKey];
    const maxOrder = list.filter((i) => i.included).reduce((m, i) => Math.max(m, i.order), -1);
    const copied: WeeklyTaskLine = {
      id: newLineId,
      ...(aliveTask ? { sourceId: aliveTask.id } : {}),
      text: taskLine.text,
      included: true,
      order: maxOrder + 1,
      origin: aliveTask ? "prev" : "manual",
      carryOver: false,
    };
    return touchDraft(draft, { [listKey]: [...list, copied] });
  }

  if (listKey === "agendas") {
    const agLine = raw as WeeklyAgendaLine;
    const aliveTask = agLine.sourceId ? taskOf(project, agLine.sourceId) : undefined;
    const maxOrder = draft.agendas.filter((i) => i.included).reduce((m, i) => Math.max(m, i.order), -1);
    const copied: WeeklyAgendaLine = {
      ...structuredClone(agLine),
      id: newLineId,
      sourceId: aliveTask ? aliveTask.id : undefined,
      included: true,
      order: maxOrder + 1,
      origin: aliveTask ? "prev" : "manual",
      carryOver: false,
    };
    return touchDraft(draft, { agendas: [...draft.agendas, copied] });
  }

  if (listKey === "issues") {
    const issLine = raw as WeeklyIssueLine;
    const aliveRecord = issLine.sourceId ? (project.records ?? []).find((r) => r.id === issLine.sourceId) : undefined;
    const maxOrder = draft.issues.filter((i) => i.included).reduce((m, i) => Math.max(m, i.order), -1);
    const copied: WeeklyIssueLine = {
      ...structuredClone(issLine),
      id: newLineId,
      sourceId: aliveRecord ? aliveRecord.id : undefined,
      included: true,
      order: maxOrder + 1,
      origin: aliveRecord ? "prev" : "manual",
      carryOver: false,
      edited: true,
    };
    return touchDraft(draft, { issues: [...draft.issues, copied] });
  }

  const reqLine = raw as WeeklyRequestLine;
  const aliveRecord = reqLine.sourceId ? (project.records ?? []).find((r) => r.id === reqLine.sourceId) : undefined;
  const maxOrder = draft.requests.filter((i) => i.included).reduce((m, i) => Math.max(m, i.order), -1);
  const copied: WeeklyRequestLine = {
    ...structuredClone(reqLine),
    id: newLineId,
    sourceId: aliveRecord ? aliveRecord.id : undefined,
    included: true,
    order: maxOrder + 1,
    origin: aliveRecord ? "prev" : "manual",
    carryOver: false,
    edited: true,
  };
  return touchDraft(draft, { requests: [...draft.requests, copied] });
}

/* ------------------------------------------------------------------------------------------------
 * 이력 표 및 마감 문서(Snapshot) 조립
 * ---------------------------------------------------------------------------------------------- */

export function historyPointFromSnapshotReport(
  statusDate: ISODate,
  report: WeeklyV2WeekSnapshot,
): WeeklyV2HistoryPoint {
  const hasBaseline = report.hasBaseline !== false;
  const openIssues = (report.issues ?? []).filter((i) => i.status !== "resolved").length;
  const resolvedIssues = (report.issues ?? []).filter((i) => i.status === "resolved").length;
  const pendingRequests = (report.requests ?? []).filter((r) => r.status !== "approved").length;
  return {
    weekId: statusDate,
    statusDate,
    weekLabel: shortWeekLabel(statusDate),
    period: report.periodLabel,
    planProgress: report.planProgress,
    actualProgress: report.actualProgress,
    gap: hasBaseline ? report.actualProgress - report.planProgress : 0,
    hasBaseline,
    openIssues,
    resolvedIssues,
    pendingRequests,
    status: report.statusVerdict,
    statusLabel: report.statusSummary,
    isDraft: false,
  };
}

export function historyPointFromDraft(draft: WeeklyReportDraft): WeeklyV2HistoryPoint {
  const includedIssues = draft.issues.filter((i) => i.included);
  const includedRequests = draft.requests.filter((r) => r.included);
  const openIssues = includedIssues.filter((i) => i.status !== "resolved").length;
  const resolvedIssues = includedIssues.filter((i) => i.status === "resolved").length;
  const pendingRequests = includedRequests.filter((r) => r.status !== "approved").length;
  return {
    weekId: draft.statusDate,
    statusDate: draft.statusDate,
    weekLabel: shortWeekLabel(draft.statusDate),
    period: draft.periodLabel,
    planProgress: draft.planProgress,
    actualProgress: draft.actualProgress,
    gap: draft.hasBaseline ? draft.actualProgress - draft.planProgress : 0,
    hasBaseline: draft.hasBaseline,
    openIssues,
    resolvedIssues,
    pendingRequests,
    status: draft.statusVerdict,
    statusLabel: draft.statusSummary,
    isDraft: true,
  };
}

/**
 * 주차별 누적 공정 이력을 만든다.
 * - 과거 줄은 각 주 마감본(최신 버전)에 저장된 공정·이슈·요청 수에서 복사한다.
 * - 마감되지 않은 과거 주는 이력 표에 넣지 않는다.
 * - 현재 기준일이 아직 초안이면 현재 초안 줄만 초안 숫자로 미리 보여 준다(isDraft: true).
 */
export function buildWeeklyHistoryFromProject(
  project: Project,
  currentDraft?: WeeklyReportDraft | null,
): WeeklyV2HistoryPoint[] {
  const byDate = new Map<ISODate, WeeklyReportSnapshot>();
  for (const snap of project.weeklySnapshots ?? []) {
    const prev = byDate.get(snap.statusDate);
    if (!prev || (snap.version ?? 1) >= (prev.version ?? 1)) {
      byDate.set(snap.statusDate, snap);
    }
  }

  const closedDates = [...byDate.keys()].sort();
  const rows: WeeklyV2HistoryPoint[] = [];
  for (const date of closedDates) {
    const snap = byDate.get(date)!;
    const report = readFrozenReport(snap);
    if (!report) continue;
    rows.push(historyPointFromSnapshotReport(date, report));
  }

  const currentStatusDate = project.statusDate;
  const currentIsClosed = byDate.has(currentStatusDate);
  if (!currentIsClosed) {
    const draft = currentDraft?.statusDate === currentStatusDate ? currentDraft : project.weeklyDrafts?.[currentStatusDate];
    if (draft) {
      rows.push(historyPointFromDraft(draft));
    }
  }

  rows.sort((a, b) => ((a.statusDate ?? a.weekId) < (b.statusDate ?? b.weekId) ? -1 : (a.statusDate ?? a.weekId) > (b.statusDate ?? b.weekId) ? 1 : 0));
  return rows;
}

/**
 * 초안(WeeklyReportDraft)을 시트/PDF 렌더링 및 마감 저장용 WeeklyV2WeekSnapshot으로 변환한다.
 * 포함하지 않은 제안 함(included === false)은 제외하며, 비용(costEvm)과 WBS는 섹션이 꺼져 있어도 포함한다.
 */
export function buildReportFromDraft(
  draft: WeeklyReportDraft,
  project: Project,
  closedAt?: string,
): WeeklyV2WeekSnapshot {
  const sortIncluded = <T extends { included: boolean; order: number }>(items: readonly T[]): T[] =>
    items
      .filter((item) => item.included)
      .sort((a, b) => a.order - b.order)
      .map((item, idx) => ({ ...structuredClone(item), order: idx }));

  const prevTaskLines = sortIncluded(draft.prevTasks);
  const currentTaskLines = sortIncluded(draft.currentTasks);
  const nextTaskLines = sortIncluded(draft.nextTasks);
  const agendaLines = sortIncluded(draft.agendas);
  const issueLines = sortIncluded(draft.issues);
  const requestLines = sortIncluded(draft.requests);
  const wbsLines = sortIncluded(draft.wbs);
  const agingNote = computeAgingNoteFromIssues(issueLines, draft.statusDate);

  const reportTitle = draft.title.trim() || suggestPeriodName(draft.statusDate);
  const reportNo = formatReportNo(draft.statusDate, draft.version);

  const snapshotReport: WeeklyV2WeekSnapshot = {
    weekId: `live:${draft.statusDate}`,
    weekNumber: 1,
    weekLabel: reportTitle,
    title: reportTitle,
    statusDate: draft.statusDate,
    periodLabel: draft.periodLabel,
    asOfDate: draft.asOfDate,
    reportNo,
    version: formatDate(draft.statusDate, "numeric"),
    versionNumber: draft.version,
    ...(closedAt ? { closedAt } : {}),
    projectName: draft.projectName,
    projectPeriod: draft.projectPeriod,
    clientPm: draft.clientPm || "발주 역할이 없습니다.",
    vendorPm: draft.vendorPm || "—",
    author: draft.author || "나",
    approvers: [],
    sections: { ...draft.sections },
    planProgress: draft.planProgress,
    actualProgress: draft.actualProgress,
    hasBaseline: draft.hasBaseline,
    computedVerdict: draft.computedVerdict,
    statusVerdict: draft.statusVerdict,
    verdictEdited: draft.verdictEdited,
    statusSummary: draft.statusSummary,
    ...(draft.weekMemo?.trim() ? { weekMemo: draft.weekMemo.trim() } : {}),
    agingNote,
    prevTasksRange: draft.prevTasksRange,
    prevTasks: prevTaskLines.map((l) => l.text),
    prevTaskLines,
    currentTasksRange: draft.currentTasksRange,
    currentTasks: currentTaskLines.map((l) => l.text),
    currentTaskLines,
    nextTasksRange: draft.nextTasksRange,
    nextTasks: nextTaskLines.map((l) => l.text),
    nextTaskLines,
    agendas: agendaLines.map((a) => ({
      id: a.id,
      sourceId: a.sourceId,
      date: a.date,
      rawDate: a.rawDate,
      title: a.title,
      owner: a.owner,
      type: a.type,
      status: a.status,
      origin: a.origin,
      carryOver: a.carryOver,
      missingSource: a.missingSource,
    })),
    agendaLines,
    issues: issueLines.map((i) => ({
      id: i.id,
      sourceId: i.sourceId,
      code: i.code,
      date: i.date,
      openedDate: i.openedDate,
      title: i.title,
      severity: i.severity,
      owner: i.owner,
      action: i.action,
      status: i.status,
      origin: i.origin,
      carryOver: i.carryOver,
      missingSource: i.missingSource,
    })),
    issueLines,
    requests: requestLines.map((r) => ({
      id: r.id,
      sourceId: r.sourceId,
      code: r.code,
      date: r.date,
      title: r.title,
      neededBy: r.neededBy,
      target: r.target,
      status: r.status,
      impact: r.impact,
      origin: r.origin,
      carryOver: r.carryOver,
      missingSource: r.missingSource,
    })),
    requestLines,
    wbs: wbsLines.map(({ included: _inc, order: _ord, ...rest }) => rest),
    wbsLines,
    costEvm: structuredClone(draft.costEvm),
    ...(draft.costNote?.trim() ? { costNote: draft.costNote.trim() } : {}),
  };

  // 스냅샷 안에 마감 시점까지의 이력 줄 복사본도 함께 저장한다.
  const pastHistory = buildWeeklyHistoryFromProject(project, null).filter((h) => (h.statusDate ?? h.weekId) !== draft.statusDate);
  const thisPoint = historyPointFromSnapshotReport(draft.statusDate, snapshotReport);
  snapshotReport.history = [...pastHistory, thisPoint].sort((a, b) =>
    (a.statusDate ?? a.weekId) < (b.statusDate ?? b.weekId) ? -1 : 1,
  );

  return snapshotReport;
}

/**
 * 플랜에 저장된 모든 주차(마감본 기준일 + 초안 기준일 + 현재 플랜 기준일) 목록을 기준일 오름차순으로 돌려준다.
 */
export interface WeeklyEntryMeta {
  statusDate: ISODate;
  label: string;
  state: "closed" | "draft" | "none";
  /** 기준일이 현재 플랜 기준일보다 이전인데 아직 마감 전(초안)이면 true */
  isUnclosedPast: boolean;
  latestVersion: number;
  versions: number[];
}

export function listWeeklyEntries(project: Project): WeeklyEntryMeta[] {
  const dates = new Set<ISODate>();
  dates.add(project.statusDate);

  const versionsByDate = new Map<ISODate, number[]>();
  for (const snap of project.weeklySnapshots ?? []) {
    if (!isISODate(snap.statusDate)) continue;
    dates.add(snap.statusDate);
    const list = versionsByDate.get(snap.statusDate) ?? [];
    const v = snap.version ?? 1;
    if (!list.includes(v)) list.push(v);
    versionsByDate.set(snap.statusDate, list);
  }

  for (const key of Object.keys(project.weeklyDrafts ?? {})) {
    if (isISODate(key)) dates.add(key);
  }

  const sorted = [...dates].sort();
  return sorted.map((statusDate) => {
    const versions = (versionsByDate.get(statusDate) ?? []).sort((a, b) => a - b);
    const hasDraft = !!project.weeklyDrafts?.[statusDate];
    const hasClosed = versions.length > 0;
    const state: WeeklyEntryMeta["state"] = hasDraft ? "draft" : hasClosed ? "closed" : "none";
    const latestVersion = hasDraft
      ? (project.weeklyDrafts![statusDate]!.version ?? 1)
      : hasClosed
        ? versions[versions.length - 1]!
        : 1;
    return {
      statusDate,
      label: shortWeekLabel(statusDate),
      state,
      isUnclosedPast: statusDate < project.statusDate && !hasClosed,
      latestVersion,
      versions,
    };
  });
}
